import { Skeleton, Typography } from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, FlexColBox } from "../../common/components";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import { spacing, Styles } from "../../styles/theme";
import Avatar from "../../common/components/Avatar/Avatar";
import { useSelector } from "../../common/hooks/useSelector";
import { EditableTextInput } from "../../common/components/Input/EditableTextInput";
import { UpdatePasswordInput } from "./UpdatePasswordInput";
import AuthService from "../../service/authService";
import { toast } from "react-toastify";
import { useAuth } from "../../common/hooks/useAuth";
import { useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { Button } from "../../common/components";
import { getInitials } from "../../utils/initialsUtils";

const styles: Styles = {
  container: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    flex: "1",
    padding: {
      xs: `32px 48px ${spacing.xlMobile}`,
      lg: `32px 48px ${spacing.xlDesktop}`,
    },
    overflow: "auto",
    gap: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
  },
  avatarSection: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  avatarControls: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
};

const UserProfile = () => {
  usePageTitle("pages.user_profile.title");
  const { t } = useTranslation();
  const userInfos = useSelector((state) => state.userInfo);
  const { refetchUserInfo } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("email_token");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isRemovingImage, setIsRemovingImage] = useState(false);

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const validateImageFile = (file: File): boolean => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      toast.error(t("user_profile.invalid_image_type"));
      return false;
    }

    if (file.size > maxSize) {
      toast.error(t("user_profile.image_too_large"));
      return false;
    }

    return true;
  };

  const onUploadProfileImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!validateImageFile(file)) {
      event.target.value = "";
      return;
    }

    setIsUploadingImage(true);
    try {
      const base64Image = await convertFileToBase64(file);
      await AuthService.uploadUserProfileImage(base64Image, file.name);
      toast.success(t("user_profile.profile_image_updated"));
      await refetchUserInfo();
    } catch (error) {
      console.error(error);
      toast.error(t("user_profile.profile_image_update_error"));
    } finally {
      setIsUploadingImage(false);
      event.target.value = "";
    }
  };

  const onRemoveProfileImage = async () => {
    setIsRemovingImage(true);
    try {
      await AuthService.removeUserProfileImage();
      toast.success(t("user_profile.profile_image_removed"));
      await refetchUserInfo();
    } catch (error) {
      console.error(error);
      toast.error(t("user_profile.profile_image_remove_error"));
    } finally {
      setIsRemovingImage(false);
    }
  };

  const onUpdateName = async ({
    firstName,
    lastName,
  }: {
    firstName?: string;
    lastName?: string;
  }) => {
    try {
      await AuthService.updateUserName(firstName, lastName);
      toast.success(
        firstName
          ? t("user_profile.first_name_updated")
          : t("user_profile.last_name_updated")
      );
      refetchUserInfo();
    } catch (error) {
      console.error(error);
      toast.error(
        firstName
          ? t("user_profile.first_name_update_error")
          : t("user_profile.last_name_update_error")
      );
    }
  };

  const onUpdateEmail = async ({ email }: { email: string }) => {
    try {
      await AuthService.updateUserEmail(email);
      toast.success(t("user_profile.email_confirmation_sent"));
      await refetchUserInfo();
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.data?.error?.message === "EMAIL_ALREADY_IN_USE") {
          toast.error(t("user_profile.email_already_exists"));
          return;
        }
      }
      toast.error(t("user_profile.email_update_error"));
    }
  };

  const onConfirmEmailUpdate = async () => {
    if (!token) {
      return;
    }
    try {
      await AuthService.confirmEmailUpdate(token);
      toast.success(t("user_profile.email_updated"));
      await refetchUserInfo();
      setSearchParams({});
    } catch (error) {
      console.error(error);
      toast.error(t("user_profile.email_update_error"));
    }
  };

  useEffect(() => {
    if (token) {
      onConfirmEmailUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Box sx={styles.container}>
      <Typography variant="title">{t("user_profile.title")}</Typography>
      <FlexColBox
        sx={styles.content}
        gap={{ xs: spacing.largeMobile, lg: spacing.largeDesktop }}
      >
        <Box sx={styles.avatarSection}>
          <Typography variant="textRegularBold">
            {t("user_profile.avatar")}
          </Typography>
          <Box>
            {userInfos.firstName && userInfos.lastName ? (
              <Avatar
                size={"xl"}
                userId={userInfos.id}
                imageUrl={userInfos.imageUrl}
                initials={getInitials(userInfos.firstName, userInfos.lastName)}
              />
            ) : (
              <Skeleton variant="circular" width={60} height={60} />
            )}
          </Box>
          <Box sx={styles.avatarControls}>
            <input
              type="file"
              id="profile-image-upload"
              accept="image/*"
              onChange={onUploadProfileImage}
              style={{ display: "none" }}
            />
            <label htmlFor="profile-image-upload">
              <Button
                variant="outlined"
                component="span"
                disabled={isUploadingImage}
                size="small"
              >
                {isUploadingImage
                  ? t("user_profile.uploading")
                  : t("user_profile.upload_image")}
              </Button>
            </label>
            {userInfos.imageUrl && (
              <Button
                variant="outlined"
                color="error"
                onClick={onRemoveProfileImage}
                disabled={isRemovingImage}
                size="small"
              >
                {isRemovingImage
                  ? t("user_profile.removing")
                  : t("user_profile.remove_image")}
              </Button>
            )}
          </Box>
        </Box>
        <Box display={"flex"} gap={"24px"}>
          <EditableTextInput
            name="firstName"
            fullWidth
            defaultValue={userInfos.firstName}
            inline={false}
            label={t("user_profile.first_name")}
            labelSx={{
              fontSize: "14px",
              fontWeight: "600",
            }}
            valueSx={{
              fontSize: "14px",
            }}
            containerSx={{
              width: "100%",
              maxWidth: "450px",
            }}
            boldLabel
            onConfirm={(value) => onUpdateName({ firstName: value })}
          />
          <EditableTextInput
            name="lastName"
            fullWidth
            defaultValue={userInfos.lastName}
            inline={false}
            label={t("user_profile.last_name")}
            labelSx={{
              fontSize: "14px",
              fontWeight: "600",
            }}
            valueSx={{
              fontSize: "14px",
            }}
            containerSx={{
              width: "100%",
              maxWidth: "450px",
            }}
            boldLabel
            onConfirm={(value) => onUpdateName({ lastName: value })}
          />
        </Box>
        <EditableTextInput
          name="email"
          fullWidth
          defaultValue={userInfos.email}
          inline={false}
          label={t("user_profile.email")}
          labelSx={{
            fontSize: "14px",
            fontWeight: "600",
          }}
          valueSx={{
            fontSize: "14px",
          }}
          containerSx={{
            width: "100%",
            maxWidth: "450px",
          }}
          boldLabel
          onConfirm={(value) => onUpdateEmail({ email: value })}
        />

        <UpdatePasswordInput />
      </FlexColBox>
    </Box>
  );
};

export default memo(UserProfile);
